import { z } from 'zod';
// ---------- Primitive shapes ----------
export const SourceSchema = z.object({
    number: z.number().int().nonnegative(),
    name: z.string(),
});
export const DestinationSchema = z.object({
    number: z.number().int().nonnegative(),
    name: z.string(),
});
export const CrosspointStateSchema = z.object({
    destination: z.number().int().nonnegative(),
    source: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
export const FavoritesSchema = z.object({
    sources: z.array(z.number().int().nonnegative()),
    destinations: z.array(z.number().int().nonnegative()),
});
// ---------- Bridge ↔ Relay messages ----------
export const BridgeHelloSchema = z.object({
    type: z.literal('hello'),
    deviceId: z.string(),
    sources: z.array(SourceSchema),
    destinations: z.array(DestinationSchema),
    state: z.array(CrosspointStateSchema),
    favorites: FavoritesSchema,
});
export const BridgeStateSchema = z.object({
    type: z.literal('state'),
    destination: z.number().int().nonnegative(),
    source: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
export const BridgeTakeResultSchema = z.object({
    type: z.literal('take.result'),
    requestId: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
});
export const BridgePingSchema = z.object({ type: z.literal('ping') });
export const BridgeToRelaySchema = z.discriminatedUnion('type', [
    BridgeHelloSchema,
    BridgeStateSchema,
    BridgeTakeResultSchema,
    BridgePingSchema,
]);
export const RelayTakeSchema = z.object({
    type: z.literal('take'),
    requestId: z.string(),
    destination: z.number().int().nonnegative(),
    source: z.number().int().nonnegative(),
    level: z.number().int().nonnegative(),
});
export const RelayRefreshSchema = z.object({ type: z.literal('refresh') });
export const RelayPongSchema = z.object({ type: z.literal('pong') });
export const RelayToBridgeSchema = z.discriminatedUnion('type', [
    RelayTakeSchema,
    RelayRefreshSchema,
    RelayPongSchema,
]);
// ---------- Relay ↔ Client messages ----------
export const ClientSnapshotSchema = z.object({
    type: z.literal('snapshot'),
    sources: z.array(SourceSchema),
    destinations: z.array(DestinationSchema),
    state: z.array(CrosspointStateSchema),
    favorites: FavoritesSchema,
    bridgeOnline: z.boolean(),
});
export const ClientStateSchema = BridgeStateSchema;
export const ClientBridgeOfflineSchema = z.object({
    type: z.literal('bridge.offline'),
    lastSeen: z.number().int().nonnegative(),
});
export const ClientBridgeOnlineSchema = z.object({ type: z.literal('bridge.online') });
export const ClientTakeResultSchema = z.object({
    type: z.literal('take.result'),
    requestId: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
});
export const RelayToClientSchema = z.discriminatedUnion('type', [
    ClientSnapshotSchema,
    ClientStateSchema,
    ClientBridgeOfflineSchema,
    ClientBridgeOnlineSchema,
    ClientTakeResultSchema,
]);
export const ClientTakeSchema = z.object({
    type: z.literal('take'),
    requestId: z.string(),
    destination: z.number().int().nonnegative(),
    source: z.number().int().nonnegative(),
    level: z.number().int().nonnegative(),
});
export const ClientPingSchema = z.object({ type: z.literal('ping') });
export const ClientToRelaySchema = z.discriminatedUnion('type', [
    ClientTakeSchema,
    ClientPingSchema,
]);
// ---------- Presets ----------
export const PresetTakeSchema = z.object({
    destination: z.number().int().nonnegative(),
    source: z.number().int().nonnegative(),
    level: z.number().int().nonnegative().default(0),
});
export const PresetSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1).max(120),
    takes: z.array(PresetTakeSchema),
    createdBy: z.string(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
});
export const PresetCreateSchema = z.object({
    name: z.string().min(1).max(120),
    takes: z.array(PresetTakeSchema).min(0).max(500),
});
export const PresetUpdateSchema = PresetCreateSchema;
export const PresetFireResultSchema = z.object({
    ok: z.boolean(),
    results: z.array(z.object({
        destination: z.number().int().nonnegative(),
        source: z.number().int().nonnegative(),
        level: z.number().int().nonnegative(),
        ok: z.boolean(),
        error: z.string().optional(),
    })),
});
// ---------- Users ----------
export const UserRoleSchema = z.enum(['admin', 'operator']);
export const UsernameSchema = z
    .string()
    .trim()
    .min(3)
    .max(32)
    .regex(/^[a-zA-Z0-9._-]+$/, 'username may contain letters, numbers, dot, underscore, hyphen');
export const PasswordSchema = z.string().min(8).max(200);
export const UserCreateSchema = z.object({
    username: UsernameSchema,
    password: PasswordSchema,
    role: UserRoleSchema,
});
export const PasswordSetSchema = z.object({
    password: PasswordSchema,
});
export const UserPatchSchema = z
    .object({
    role: UserRoleSchema.optional(),
    disabled: z.boolean().optional(),
    totpRequired: z.boolean().optional(),
    // Current authenticator code; required only when changing your own 2FA.
    totpCode: z.string().optional(),
})
    .refine((v) => v.role !== undefined || v.disabled !== undefined || v.totpRequired !== undefined, {
    message: 'must specify role, disabled or totpRequired',
});
